


import React, { useState, useEffect, useCallback } from "react";
import APIConfig from "../../Common/Configurations/APIConfig";
import Navbar from "../../Navigation/Navbar";
import Swal from "sweetalert2";
import { 
  Eye, EyeOff, Pencil, Trash2, Plus, Search, RotateCcw,
  Loader2, ArrowLeft, CheckCircle, User, Briefcase, 
  ShieldCheck, X, Phone, Mail, MapPin, Heart, 
  Globe, Info, FileText, Calendar, Lock, GraduationCap, UsersRound
} from "lucide-react";

const Agents = () => {
  // --- VIEW STATE ---
  const [view, setView] = useState("list"); 
  const [showPassword, setShowPassword] = useState(false); 
  
  // --- DATA STATES ---
  const [agentsList, setAgentsList] = useState([]);
  const [designations, setDesignations] = useState([]);
  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [inputValue, setInputValue] = useState(""); 
  const [searchTerm, setSearchTerm] = useState(""); 
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const pageSize = 10;

  const [showViewModal, setShowViewModal] = useState(false);
  const [viewAgent, setViewAgent] = useState(null);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);

  const initialFormState = {
    agentId: null,
    code: "",
    name: "",
    referenceId: null,
    designationId: "",
    branchId: "",
    fatherName: "",
    dateofBirth: "",
    gender: "Male",
    martialStatus: "Single",
    address: "",
    email: "",
    phoneNo: "",
    panCard: "",
    aadharNo: "",
    academic: "",
    technical: "",
    experience: "",
    hobbies: "",
    languagesKnown: "",
    nomineeName: "",
    nomineePhone: "",
    nomineeDateOfBirth: "",
    nomineeAddress: "",
    relationship: "",
    status: "ACTIVE",
    profileImageName: "",
    joiningDate: "",
    exitDate: null,
    notes: "",
    payMode: "Cash",
    isLogin: false,
    password: ""
  };
  
  const [formData, setFormData] = useState(initialFormState);

  // --- HELPERS ---
  const formatDisplayDate = (dateStr) => {
    if (!dateStr) return "---";
    try {
        const date = new Date(dateStr);
        if(isNaN(date.getTime())) return dateStr; 
        const d = String(date.getDate()).padStart(2, '0');
        const m = String(date.getMonth() + 1).padStart(2, '0');
        const y = date.getFullYear();
        return `${d}/${m}/${y}`;
    } catch (e) { return dateStr; }
  };

  // --- FETCHING ---
  const fetchDesignations = async () => {
    try {
        const res = await fetch(`${APIConfig.APIACTIVEURL}${APIConfig.DESIGNATION_GET_ALL}?pageSize=100`);
        const data = await res.json();
        setDesignations(data.data || []);
    } catch (e) { console.error(e); }
  };

  const fetchBranches = async () => {
    try {
        const res = await fetch(`${APIConfig.APIACTIVEURL}${APIConfig.BRANCH_GET_ALL}?pageSize=100`);
        const data = await res.json();
        setBranches(data.data || []);
    } catch (e) { console.error(e); }
  };

  const fetchAgents = useCallback(async () => {
    setLoading(true);
    try {
      const url = `${APIConfig.APIACTIVEURL}${APIConfig.AGENT_GET_ALL}?pageNumber=${currentPage}&pageSize=${pageSize}&searchTerm=${searchTerm}`;
      const response = await fetch(url);
      const result = await response.json();
      setAgentsList(result.data || []);
      setTotalPages(result.totalPages || 1);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [currentPage, searchTerm]);

  useEffect(() => {
    fetchAgents();
    fetchDesignations();
    fetchBranches();
  }, [fetchAgents]);

  // --- HANDLERS ---
  const handleSearchClick = () => { setSearchTerm(inputValue); setCurrentPage(1); };
  const handleClearSearch = () => { setInputValue(""); setSearchTerm(""); setCurrentPage(1); };

  const openForm = (agent = null) => {
    setErrors({});
    setShowPassword(false);
    if (agent) {
      setFormData({ ...agent, 
        designationId: agent.designationId || "", 
        branchId: agent.branchId || "" 
      });
    } else {
      setFormData(initialFormState);
    }
    setView("form");
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    if (errors[name]) setErrors({ ...errors, [name]: false });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    let tempErrors = {};
    if (!formData.name) tempErrors.name = true;
    if (!formData.code) tempErrors.code = true;
    if (!formData.phoneNo) tempErrors.phoneNo = true;
    
    if (Object.keys(tempErrors).length > 0) {
        setErrors(tempErrors);
        return Swal.fire("Required", "Please fill Name, Code, and Phone Number.", "warning");
    }
    
    setSaving(true);
    try {
      const res = await fetch(APIConfig.APIACTIVEURL + APIConfig.AGENT_BASE, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(formData),
      });
      const result = await res.json();
      if (result.success) {
        setView("list");
        fetchAgents();
        Swal.fire({ icon: 'success', title: 'Success', text: result.message, timer: 1500, showConfirmButton: false });
      } else {
        Swal.fire('Error', result.message, 'error');
      }
    } catch (err) { Swal.fire('Error', 'Connection failed', 'error'); }
    setSaving(false);
  };

  const handleDelete = async (id) => {
    const result = await Swal.fire({ title: 'Delete Agent?', text: 'Are you sure?', icon: 'warning', showCancelButton: true, confirmButtonColor: '#ef4444' });
    if (result.isConfirmed) {
      await fetch(`${APIConfig.APIACTIVEURL}${APIConfig.AGENT_BASE}/${id}`, { method: "DELETE" });
      fetchAgents();
    }
  };

  // --- STYLES ---
  const styles = {
    container: { padding: "40px 60px", backgroundColor: "#f4f7fe", minHeight: "calc(100vh - 70px)", fontFamily: "'Inter', sans-serif" },
    card: { backgroundColor: "#fff", borderRadius: "24px", boxShadow: "0 10px 30px rgba(0,0,0,0.02)", overflow: "hidden", border: '1px solid #f1f5f9' },
    formSection: { background: '#fff', border: '1px solid #f1f5f9', borderRadius: '24px', padding: '30px', marginBottom: '30px' },
    label: { display: "block", fontSize: "11px", fontWeight: "800", color: "#a3aed0", marginBottom: "8px", textTransform: 'uppercase', letterSpacing: '0.5px' },
    input: { padding: "14px", borderRadius: "12px", border: "1.5px solid #e2e8f0", outline: "none", fontSize: "15px", width: '100%', boxSizing: 'border-box' },
    btnPrimary: { backgroundColor: "#4f46e5", color: "#fff", border: "none", padding: "14px 28px", borderRadius: "14px", cursor: "pointer", fontWeight: "700", display: "flex", alignItems: "center", gap: "10px" },
    btnReset: { backgroundColor: "#fff", color: "#64748b", border: "1.5px solid #e2e8f0", padding: "0 24px", borderRadius: "14px", cursor: "pointer", fontWeight: "700", display: "flex", alignItems: "center", gap: "8px" },
    sectionHeader: { display:'flex', alignItems:'center', gap:'10px', fontSize:'17px', fontWeight:'900', color:'#1b2559', marginBottom:'25px' },
    statusBadge: (status) => ({
        fontSize: '10px', fontWeight: '900', padding: '4px 12px', borderRadius: '15px',
        background: status === 'ACTIVE' ? '#dcfce7' : '#f4f7fe',
        color: status === 'ACTIVE' ? '#059669' : '#a3aed0'
    }),
    toggleContainer: {
        width: '50px', height: '26px', borderRadius: '50px', position: 'relative', cursor: 'pointer', transition: '0.3s',
        display: 'inline-block', verticalAlign: 'middle'
    }
  };

  return (
    <>
      <Navbar />
      <div style={styles.container}>
        
        {/* --- VIEW: LIST --- */}
        {view === "list" && (
          <>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '35px' }}>
              <div><h1 style={{ fontSize: '30px', fontWeight: '900', color: '#1b2559', margin: 0 }}>Agents</h1></div>
              <button style={styles.btnPrimary} onClick={() => openForm()}><Plus size={20} /> New Agent</button>
            </div>

            <div style={{ display: 'flex', gap: '12px', marginBottom: '30px' }}>
                <div style={{ position: 'relative', flex: 1, maxWidth: '400px' }}>
                    <Search size={18} style={{ position: 'absolute', left: '15px', top: '15px', color: '#cbd5e1' }} />
                    <input style={{ ...styles.input, paddingLeft: '45px' }} placeholder="Search agents..." value={inputValue} onChange={(e) => setInputValue(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && handleSearchClick()} />
                </div>
                <button style={styles.btnPrimary} onClick={handleSearchClick}>Search</button>
                <button onClick={handleClearSearch} style={styles.btnReset}><RotateCcw size={18}/> Reset</button>
            </div>

            <div style={styles.card}>
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead>
                  <tr style={{ textAlign: 'left', borderBottom: '1px solid #f8fafc', background: '#fcfcfd' }}>
                    <th style={{ padding: '20px 24px', fontSize: '12px', color: '#a3aed0' }}>AGENT DETAILS</th>
                    <th style={{ padding: '20px 24px', fontSize: '12px', color: '#a3aed0' }}>LOCATION</th>
                    <th style={{ padding: '20px 24px', fontSize: '12px', color: '#a3aed0' }}>DESIGNATION</th>
                    <th style={{ padding: '20px 24px', fontSize: '12px', color: '#a3aed0' }}>STATUS</th>
                    <th style={{ padding: '20px 24px', fontSize: '12px', color: '#a3aed0', textAlign:'center' }}>ACTION</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr><td colSpan="5" style={{ textAlign:'center', padding:'80px' }}><Loader2 className="animate-spin" color="#4f46e5" /></td></tr>
                  ) : agentsList.map(a => (
                    <tr key={a.agentId} style={{ borderBottom: '1px solid #f8fafc' }}>
                      <td style={{ padding: '20px 24px' }}>
                        <div style={{ fontWeight: '800', color: '#1b2559' }}>{a.name}</div>
                        <div style={{ fontSize: '12px', color: '#a3aed0' }}>{a.code} | {a.phoneNo}</div>
                      </td>
                      <td style={{ padding: '20px 24px', color:'#1b2559', fontWeight:'700' }}>{a.address || 'N/A'}</td>
                      <td style={{ padding: '20px 24px', color:'#4f46e5', fontWeight:'700' }}>{a.designationId ? designations.find(d => d.designationId === a.designationId)?.designationName : 'General Agent'}</td>
                      <td style={{ padding: '20px 24px' }}>
                        <span style={styles.statusBadge(a.status)}>{a.status}</span>
                      </td>
                      <td style={{ padding: '20px 24px', textAlign: 'center' }}>
                        <div style={{ display: 'flex', gap: '10px', justifyContent: 'center' }}>
                            <button onClick={() => {setViewAgent(a); setShowViewModal(true)}} style={{ border: 'none', background: '#f4f7fe', color: '#1b2559', padding: '10px', borderRadius: '10px', cursor: 'pointer' }}><Eye size={16}/></button>
                            <button onClick={() => openForm(a)} style={{ border: 'none', background: '#eef2ff', color: '#4f46e5', padding: '10px', borderRadius: '10px', cursor: 'pointer' }}><Pencil size={16}/></button>
                            <button onClick={() => handleDelete(a.agentId)} style={{ border: 'none', background: '#fff1f2', color: '#ef4444', padding: '10px', borderRadius: '10px', cursor: 'pointer' }}><Trash2 size={16}/></button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div style={{ padding: '20px 24px', display: 'flex', justifyContent: 'space-between', alignItems:'center', background: '#fcfcfd' }}>
                <span style={{color:'#a3aed0', fontSize:'14px', fontWeight: '700'}}>Page {currentPage} of {totalPages}</span>
                <div style={{display:'flex', gap:'8px'}}>
                    <button disabled={currentPage === 1} onClick={() => setCurrentPage(c => c - 1)} style={styles.btnReset}><ArrowLeft size={22}/> Prev</button>
                    <button disabled={currentPage >= totalPages} onClick={() => setCurrentPage(c => c + 1)} style={styles.btnReset}>Next <ArrowLeft size={22} style={{transform: 'rotate(180deg)'}}/></button>
                </div>
              </div>
            </div>
          </>
        )}

        {/* --- VIEW: FORM --- */}
        {view === "form" && (
          <div style={{ maxWidth: '1100px', margin: '0 auto' }}>
            <button onClick={() => setView("list")} style={{ border:'none', background:'none', color:'#a3aed0', fontWeight:'700', cursor:'pointer', display:'flex', alignItems:'center', gap:'8px', marginBottom:'25px' }}>
                <ArrowLeft size={20} /> Back to Directory
            </button>
            
            <h2 style={{ fontSize: '28px', fontWeight: '900', color: '#1b2559', marginBottom: '35px' }}>{formData.agentId ? "Modify Agent Record" : "Enroll New Agent"}</h2>

            <form onSubmit={handleSubmit}>
                {/* 1. PERSONAL INFO */}
                <div style={styles.formSection}>
                    <div style={styles.sectionHeader}><User color="#4f46e5" /> Personal Information</div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '20px' }}>
                        <div><label style={styles.label}>Agent Code *</label><input name="code" style={styles.input} value={formData.code} onChange={handleInputChange} /></div>
                        <div><label style={styles.label}>Full Name *</label><input name="name" style={styles.input} value={formData.name} onChange={handleInputChange} /></div>
                        <div><label style={styles.label}>Phone Number *</label><input name="phoneNo" style={styles.input} value={formData.phoneNo} onChange={handleInputChange} /></div>
                        <div><label style={styles.label}>Email Address</label><input name="email" style={styles.input} value={formData.email} onChange={handleInputChange} /></div>
                        <div><label style={styles.label}>Father's Name</label><input name="fatherName" style={styles.input} value={formData.fatherName} onChange={handleInputChange} /></div>
                        <div><label style={styles.label}>Date of Birth</label><input type="date" name="dateofBirth" style={styles.input} value={formData.dateofBirth} onChange={handleInputChange} /></div>
                        <div><label style={styles.label}>Gender</label>
                            <select name="gender" style={styles.input} value={formData.gender} onChange={handleInputChange}>
                                <option value="Male">Male</option><option value="Female">Female</option>
                            </select>
                        </div>
                        <div><label style={styles.label}>Marital Status</label>
                            <select name="martialStatus" style={styles.input} value={formData.martialStatus} onChange={handleInputChange}>
                                <option value="Single">Single</option><option value="Married">Married</option><option value="Divorced">Divorced</option>
                            </select>
                        </div>
                        <div style={{gridColumn:'span 3'}}><label style={styles.label}>Residential Address</label><input name="address" style={styles.input} value={formData.address} onChange={handleInputChange} /></div>
                    </div>
                </div>

                {/* 2. PROFESSIONAL & KYC */}
                <div style={styles.formSection}>
                    <div style={styles.sectionHeader}><Briefcase color="#4f46e5" /> Professional & KYC</div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '20px' }}>
                        <div><label style={styles.label}>Designation</label>
                            <select name="designationId" style={styles.input} value={formData.designationId} onChange={handleInputChange}>
                                <option value="">Select Role...</option>
                                {designations.map(d => <option key={d.designationId} value={d.designationId}>{d.designationName}</option>)}
                            </select>
                        </div>
                        <div><label style={styles.label}>Branch</label>
                            <select name="branchId" style={styles.input} value={formData.branchId} onChange={handleInputChange}>
                                <option value="">Select Branch...</option>
                                {branches.map(b => <option key={b.branchId} value={b.branchId}>{b.branchName}</option>)}
                            </select>
                        </div>
                        <div><label style={styles.label}>Joining Date</label><input type="date" name="joiningDate" style={styles.input} value={formData.joiningDate} onChange={handleInputChange} /></div>
                        <div><label style={styles.label}>PAN Card</label><input name="panCard" style={styles.input} value={formData.panCard} onChange={handleInputChange} /></div>
                        <div><label style={styles.label}>Aadhar Number</label><input name="aadharNo" style={styles.input} value={formData.aadharNo} onChange={handleInputChange} /></div>
                        <div><label style={styles.label}>Pay Mode</label>
                            <select name="payMode" style={styles.input} value={formData.payMode} onChange={handleInputChange}>
                                <option value="Cash">Cash</option><option value="Bank">Bank</option><option value="Cheque">Cheque</option>
                            </select>
                        </div>
                        <div><label style={styles.label}>Status</label>
                            <select name="status" style={styles.input} value={formData.status} onChange={handleInputChange}>
                                <option value="ACTIVE">ACTIVE</option><option value="INACTIVE">INACTIVE</option>
                            </select>
                        </div>
                        <div style={{gridColumn:'span 2'}}><label style={styles.label}>Professional Experience</label><input name="experience" style={styles.input} value={formData.experience} onChange={handleInputChange} placeholder="e.g. 5 Years in Real Estate" /></div>
                    </div>
                </div>

                {/* 3. ACADEMIC & ADDITIONAL */}
                <div style={styles.formSection}>
                    <div style={styles.sectionHeader}><GraduationCap color="#4f46e5" /> Academic & Additional Details</div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
                        <div><label style={styles.label}>Academic Qualifications</label><input name="academic" style={styles.input} value={formData.academic} onChange={handleInputChange} /></div>
                        <div><label style={styles.label}>Technical Skills</label><input name="technical" style={styles.input} value={formData.technical} onChange={handleInputChange} /></div>
                        <div><label style={styles.label}>Languages Known</label><input name="languagesKnown" style={styles.input} value={formData.languagesKnown} onChange={handleInputChange} /></div>
                        <div><label style={styles.label}>Hobbies</label><input name="hobbies" style={styles.input} value={formData.hobbies} onChange={handleInputChange} /></div>
                        <div style={{gridColumn:'span 2'}}><label style={styles.label}>Important Notes / Remarks</label><textarea name="notes" style={{...styles.input, height:'80px'}} value={formData.notes} onChange={handleInputChange} /></div>
                    </div>
                </div>

                {/* 4. NOMINEE INFORMATION */}
                <div style={styles.formSection}>
                    <div style={styles.sectionHeader}><UsersRound color="#4f46e5" /> Nominee Information</div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '20px' }}>
                        <div><label style={styles.label}>Nominee Name</label><input name="nomineeName" style={styles.input} value={formData.nomineeName} onChange={handleInputChange} /></div>
                        <div><label style={styles.label}>Relationship</label><input name="relationship" style={styles.input} value={formData.relationship} onChange={handleInputChange} /></div>
                        <div><label style={styles.label}>Nominee Phone</label><input name="nomineePhone" style={styles.input} value={formData.nomineePhone} onChange={handleInputChange} /></div>
                        <div><label style={styles.label}>Nominee Date of Birth</label><input type="date" name="nomineeDateOfBirth" style={styles.input} value={formData.nomineeDateOfBirth} onChange={handleInputChange} /></div>
                        <div style={{gridColumn:'span 2'}}><label style={styles.label}>Nominee Address</label><input name="nomineeAddress" style={styles.input} value={formData.nomineeAddress} onChange={handleInputChange} /></div>
                    </div>
                </div>

                {/* 5. PORTAL ACCESS */}
                <div style={styles.formSection}>
                    <div style={styles.sectionHeader}><ShieldCheck color="#4f46e5" /> Portal Access</div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '20px', alignItems: 'end' }}>
                        <div>
                            <label style={styles.label}>Website Login Access</label>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                <div 
                                    onClick={() => setFormData({ ...formData, isLogin: !formData.isLogin })}
                                    style={{ ...styles.toggleContainer, backgroundColor: formData.isLogin ? '#4f46e5' : '#cbd5e1' }}
                                >
                                    <div style={{ 
                                        width: '18px', height: '18px', backgroundColor: '#fff', borderRadius: '50%', position: 'absolute', 
                                        top: '4px', left: formData.isLogin ? '28px' : '4px', transition: '0.2s' 
                                    }} />
                                </div>
                                <span style={{ fontSize: '14px', fontWeight: '700', color: formData.isLogin ? '#4f46e5' : '#a3aed0' }}>
                                    {formData.isLogin ? "Enabled" : "Disabled"}
                                </span>
                            </div>
                        </div>

                        {formData.isLogin && (
                            <div style={{ position: 'relative' }}>
                                <label style={styles.label}>Portal Password</label>
                                <div style={{ position: 'relative' }}>
                                    <Lock size={16} style={{ position: 'absolute', left: '15px', top: '16px', color: '#cbd5e1' }} />
                                    <input 
                                        type={showPassword ? "text" : "password"}
                                        name="password" 
                                        placeholder="Set password"
                                        style={{ ...styles.input, paddingLeft: '45px', paddingRight: '45px' }} 
                                        value={formData.password} 
                                        onChange={handleInputChange} 
                                    />
                                    {showPassword ? 
                                        <EyeOff size={18} onClick={() => setShowPassword(false)} style={{ position: 'absolute', right: '15px', top: '15px', color: '#64748b', cursor: 'pointer' }} /> : 
                                        <Eye size={18} onClick={() => setShowPassword(true)} style={{ position: 'absolute', right: '15px', top: '15px', color: '#cbd5e1', cursor: 'pointer' }} />
                                    }
                                </div>
                            </div>
                        )}
                        
                        <div>
                            <label style={styles.label}>Login Username</label>
                            <input 
                                disabled
                                style={{ ...styles.input, backgroundColor: '#f8fafc', color: '#94a3b8', cursor: 'not-allowed' }} 
                                value={formData.phoneNo || "Set Phone Number Above"} 
                            />
                        </div>
                    </div>
                </div>

                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '15px', paddingBottom: '80px' }}>
                    <button type="button" onClick={() => setView("list")} style={styles.btnReset}>Cancel</button>
                    <button type="submit" disabled={saving} style={styles.btnPrimary}>
                        {saving ? <Loader2 className="animate-spin" /> : <CheckCircle />} {saving ? "Saving..." : "Confirm & Save Agent"}
                    </button>
                </div>
            </form>
          </div>
        )}

        {/* --- VIEW PROFILE MODAL --- */}
        {showViewModal && viewAgent && (
            <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(15, 23, 42, 0.6)', backdropFilter: 'blur(4px)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 5000 }}>
                <div style={{ backgroundColor: '#fff', padding: '40px', borderRadius: '32px', width: '800px', maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 25px 50px rgba(0,0,0,0.2)' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '30px' }}>
                        <h2 style={{ margin: 0, fontWeight: '900', color: '#1b2559' }}>Agent Profile</h2>
                        <X size={24} style={{ cursor: 'pointer', color: '#cbd5e1' }} onClick={() => setShowViewModal(false)} />
                    </div>
                    
                    <div style={{ display: 'grid', gap: '25px' }}>
                        <div style={{ padding: '30px', background: 'linear-gradient(135deg, #f4f7fe 0%, #fff 100%)', borderRadius: '24px', textAlign: 'center', border: '1px solid #eef2ff' }}>
                            <div style={{ fontSize: '26px', fontWeight: '900', color: '#1b2559', marginBottom: '5px' }}>{viewAgent.name}</div>
                            {/* <div style={{ color: '#4f46e5', fontWeight: '800', textTransform: 'uppercase', fontSize: '12px', letterSpacing: '1px' }}>{viewAgent.code} • {viewAgent.status}</div> */}
                            <div style={{ 
    color: '#4f46e5', 
    fontWeight: '800', 
    textTransform: 'uppercase', 
    fontSize: '12px', 
    letterSpacing: '1px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '10px' 
}}>
    {viewAgent.code} • {viewAgent.status}
    
    {/* --- VISUAL LOGIN STATUS BADGE --- */}
    <span style={{
        padding: '2px 10px',
        borderRadius: '6px',
        fontSize: '10px',
        fontWeight: '900',
        background: viewAgent.isLogin ? '#dcfce7' : '#fee2e2',
        color: viewAgent.isLogin ? '#059669' : '#ef4444',
        border: `1px solid ${viewAgent.isLogin ? '#bbf7d0' : '#fecaca'}`
    }}>
        {viewAgent.isLogin ? "LOGIN ACTIVE" : "LOGIN INACTIVE"}
    </span>
</div>
                        </div>

                        {/* INFO GRID */}
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
                            <div><label style={styles.label}>Personal Details</label>
                                <div style={{background:'#f8fafc', padding:'15px', borderRadius:'15px', fontSize:'13px'}}>
                                    <b>Father:</b> {viewAgent.fatherName || '---'}<br/>
                                    <b>DOB:</b> {formatDisplayDate(viewAgent.dateofBirth)}<br/>
                                    <b>Gender:</b> {viewAgent.gender} / {viewAgent.martialStatus}<br/>
                                    <b>Address:</b> {viewAgent.address || 'N/A'}
                                </div>
                            </div>
                            <div><label style={styles.label}>Contact & Portal</label>
                                <div style={{background:'#f8fafc', padding:'15px', borderRadius:'15px', fontSize:'13px'}}>
                                    <b>Phone:</b> {viewAgent.phoneNo}<br/>
                                    <b>Email:</b> {viewAgent.email || 'N/A'}<br/>
                                    <b>Access:</b> {viewAgent.isLogin ? 'ENABLED' : 'DISABLED'}<br/>
                                    <b>KYC:</b> {viewAgent.panCard || '---'} / {viewAgent.aadharNo || '---'}
                                </div>
                            </div>
                            <div><label style={styles.label}>Academic & Professional</label>
                                <div style={{background:'#f8fafc', padding:'15px', borderRadius:'15px', fontSize:'13px'}}>
                                    <b>Academic:</b> {viewAgent.academic || '---'}<br/>
                                    <b>Technical:</b> {viewAgent.technical || '---'}<br/>
                                    <b>Experience:</b> {viewAgent.experience || '---'}<br/>
                                    <b>Languages:</b> {viewAgent.languagesKnown || '---'}
                                </div>
                            </div>
                            <div><label style={styles.label}>Nominee Information</label>
                                <div style={{background:'#f8fafc', padding:'15px', borderRadius:'15px', fontSize:'13px'}}>
                                    <b>Name:</b> {viewAgent.nomineeName || '---'}<br/>
                                    <b>Relation:</b> {viewAgent.relationship || '---'}<br/>
                                    <b>Phone:</b> {viewAgent.nomineePhone || '---'}<br/>
                                    <b>Joining:</b> {formatDisplayDate(viewAgent.joiningDate)}
                                </div>
                            </div>
                        </div>

                        <div style={{ padding: '20px', background: '#f4f7fe', borderRadius: '20px' }}>
                            <label style={styles.label}>Remarks / Notes</label>
                            <div style={{fontWeight: '600', color: '#1b2559', fontSize:'13px'}}>{viewAgent.notes || 'No notes available.'}</div>
                        </div>
                    </div>
                    <button onClick={() => setShowViewModal(false)} style={{ ...styles.btnPrimary, width: '100%', marginTop: '35px', justifyContent: 'center', height: '54px' }}>Close Profile</button>
                </div>
            </div>
        )}
      </div>
    </>
  );
};

export default Agents;